import React, { Component } from "react";
import { Row, Col, Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { withRouter } from "react-router-dom";
import { withAppContext } from "../store/contextStore";
import HomeBuddha from "../common/HomeBuddha";

const NAVY   = "#1A2280";
const GOLD   = "#D4921A";
const GREEN  = "#2D6A4F";
const MUTED  = "#718096";
const BORDER = "#E2E8F0";

// Stage 1: standalone public route for the Home Portal, reachable directly
// from the header nav (not just buried in My Tools). Renders the exact same
// <HomeBuddha /> component the "Home Buddha" tab in ViewProfile.js uses —
// nothing about that tab changes, this is just a second entry point to the
// same component.
//
// Stage 2: anonymous visitors can browse every section/subsection and use
// AI chat freely. Only "Set Up My Home" (creating a home profile) and the
// Home Health Check-Up quiz (gated inside HomeBuddha.js itself) require
// login. Optional /:sectionKey/:subId route params let an ad or post deep
// link straight into a specific subsection (e.g. water source quality).
//
// Marketing hook (below) is pulled from the "Home Portal" brand one-pager
// and only shows for logged-out visitors, same pattern as ValueTrackerPage.js.
class HomePortalPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showSetupMyHome: false,
    };
  }

  handleSetupClick = () => {
    const userLogin = localStorage.getItem("userLogin");
    if (!userLogin) {
      this.props.history.push({
        pathname: "/login",
        state: { from: this.props.location.pathname },
      });
      return;
    }
    this.setState({ showSetupMyHome: true });
  };

  renderHook() {
    const BADGES = [
      { icon: "🏠", label: "Organize Everything" },
      { icon: "🛡️", label: "Protect What Matters" },
      { icon: "❤️", label: "Improve With Confidence" },
    ];
    const TOOLS = [
      { icon: "🔧", title: "Smart Maintenance Reminders", desc: "Never miss what matters. Get personalized reminders to keep your home running smoothly and avoid costly repairs." },
      { icon: "📸", title: "AI Photo Analysis", desc: "Take a photo, get answers. Our AI analyzes your furnace, water heater, electrical panel & more to flag issues." },
      { icon: "💬", title: "Ask My Home Anything", desc: "Get instant AI answers about your home, systems, repairs, upgrades, safety and more — 24/7." },
      { icon: "📈", title: "Track & Protect Your Investment", desc: "See your home's value over time and track the improvements and maintenance that build equity." },
      { icon: "🗂️", title: "Home Documents Vault", desc: "Store warranties, manuals, receipts, permits and inspections — all safe and easy to find." },
      { icon: "🛒", title: "Access Home Products", desc: "Find recommended products for your home, with exclusive deals on what you need to maintain and improve it." },
      { icon: "🤝", title: "Trusted Service Providers", desc: "Connect with rated local pros you can trust for any job — big or small." },
      { icon: "🩺", title: "Home Health Score", desc: "Get a score for your home's health, safety & efficiency, and see ideas to make it even better." },
    ];
    const PORTAL_BENEFITS = [
      { icon: "🛟", label: "Stay Safe", desc: "Monitor systems, get alerts, and address issues before they become problems." },
      { icon: "💵", label: "Save Money", desc: "Prevent surprises and make smarter maintenance decisions." },
      { icon: "📊", label: "Build Value", desc: "Track improvements and maintenance that increase your home's value." },
      { icon: "🎯", label: "Make It Yours", desc: "Personalized tools, recommendations, and insights for YOUR home." },
      { icon: "🧘", label: "Peace of Mind", desc: "Everything you need to manage your home — anytime, anywhere." },
    ];
    const BOTTOM_STRIP = [
      { icon: "🧠", label: "Know More, Worry Less", desc: "Get insights and confidence about your home." },
      { icon: "🔔", label: "Always One Step Ahead", desc: "Reminders and alerts keep you ahead of every season." },
      { icon: "🤝", label: "Connect With The Best", desc: "Find trusted pros and get the job done right." },
      { icon: "🏷️", label: "Exclusive Savings", desc: "Access special offers on quality products." },
    ];

    return (
      <div style={{ marginBottom: "18px" }}>
        {/* Hero */}
        <div
          style={{
            background: `linear-gradient(135deg, ${NAVY} 0%, #2A3390 100%)`,
            borderRadius: "16px",
            padding: "30px 26px",
            marginBottom: "16px",
            boxShadow: "0 6px 24px rgba(26,34,128,0.18)",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "40px", marginBottom: "6px" }}>🧘</div>
          <div style={{ color: GOLD, fontWeight: 700, fontSize: "12px", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: "6px" }}>
            My Home Buddha · Smarter Tools. Better Homes.
          </div>
          <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "13px", marginBottom: "2px" }}>
            Your Home. All In One Place.
          </div>
          <h1 style={{ color: "white", fontWeight: 900, fontSize: "26px", margin: "0 0 4px", lineHeight: 1.25 }}>
            The Home Portal
          </h1>
          <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "14px", marginBottom: "16px", fontStyle: "italic" }}>
            The Smartest Way to Manage Your Home
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap", marginBottom: "14px" }}>
            {BADGES.map(b => (
              <div key={b.label} style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "50px", padding: "6px 14px", color: "white", fontSize: "12px", fontWeight: 700 }}>
                {b.icon} {b.label}
              </div>
            ))}
          </div>
          <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "12px", maxWidth: "480px", margin: "0 auto" }}>
            Your personalized command center for a safer, healthier, more valuable home.
          </div>
        </div>

        {/* Tools grid */}
        <div style={{ marginBottom: "8px", textAlign: "center" }}>
          <div style={{ fontSize: "15px", fontWeight: 800, color: NAVY }}>Powerful Tools Built For Homeowners</div>
        </div>
        <Row gutter={[12, 12]} style={{ marginBottom: "16px" }}>
          {TOOLS.map(t => (
            <Col key={t.title} xs={24} sm={12} md={6}>
              <div style={{ background: "white", border: `1px solid ${BORDER}`, borderRadius: "12px", padding: "16px", height: "100%" }}>
                <div style={{ fontSize: "24px", marginBottom: "6px" }}>{t.icon}</div>
                <div style={{ fontWeight: 700, color: NAVY, fontSize: "12px", marginBottom: "4px" }}>{t.title}</div>
                <div style={{ fontSize: "11px", color: MUTED, lineHeight: 1.6 }}>{t.desc}</div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Sample dashboard preview */}
        <div style={{ background: "white", border: `1px solid ${BORDER}`, borderRadius: "12px", padding: "18px 20px", marginBottom: "16px" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "12px", textAlign: "center" }}>
            Your Home Portal Dashboard — Sample Preview
          </div>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={8} style={{ textAlign: "center" }}>
              <div style={{ fontSize: "11px", color: MUTED, marginBottom: "2px" }}>Home Health Score</div>
              <div style={{ fontSize: "28px", fontWeight: 900, color: GREEN }}>85</div>
              <div style={{ fontSize: "11px", color: GREEN, fontWeight: 700 }}>Great!</div>
              <div style={{ fontSize: "10px", color: MUTED, marginTop: "4px" }}>Safety · Systems · Maintenance · Efficiency · Environment</div>
            </Col>
            <Col xs={24} sm={8} style={{ textAlign: "center" }}>
              <div style={{ fontSize: "11px", color: MUTED, marginBottom: "2px" }}>Home Value Tracker</div>
              <div style={{ fontSize: "22px", fontWeight: 900, color: NAVY }}>$725,000</div>
              <div style={{ fontSize: "11px", color: GREEN, fontWeight: 700 }}>+$48,500 (7.2%) vs 6 mo ago</div>
            </Col>
            <Col xs={24} sm={8} style={{ textAlign: "center" }}>
              <div style={{ fontSize: "11px", color: MUTED, marginBottom: "2px" }}>Upcoming Maintenance</div>
              <div style={{ fontSize: "12px", color: "#1A202C", lineHeight: 1.9 }}>
                Change AC Filter — 10 days<br />
                Inspect Sump Pump — 21 days<br />
                Service Water Heater — 45 days
              </div>
            </Col>
          </Row>
        </div>

        {/* Portal benefits */}
        <div style={{ background: `linear-gradient(135deg, ${GOLD}, #B8790F)`, borderRadius: "12px", padding: "18px 22px", marginBottom: "16px" }}>
          <div style={{ color: "#2A1600", fontWeight: 800, fontSize: "15px", marginBottom: "12px", textAlign: "center" }}>
            All The Tools You Need. One Powerful Portal.
          </div>
          <Row gutter={[8, 8]}>
            {PORTAL_BENEFITS.map(b => (
              <Col key={b.label} xs={12} sm={8} md={4} style={{ flex: "1 1 auto" }}>
                <div style={{ background: "rgba(255,255,255,0.5)", borderRadius: "8px", padding: "10px 8px", textAlign: "center", height: "100%" }}>
                  <div style={{ fontSize: "18px" }}>{b.icon}</div>
                  <div style={{ fontSize: "11px", color: "#2A1600", fontWeight: 800, marginTop: "4px" }}>{b.label}</div>
                  <div style={{ fontSize: "10px", color: "#2A1600", opacity: 0.8, marginTop: "2px", lineHeight: 1.4 }}>{b.desc}</div>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* Testimonial */}
        <div style={{ textAlign: "center", padding: "14px 20px", marginBottom: "16px" }}>
          <div style={{ fontSize: "13px", color: "#1A202C", fontStyle: "italic", marginBottom: "6px" }}>
            "My Home Portal organized everything and my home running better than ever."
          </div>
          <div style={{ fontSize: "11px", color: MUTED }}>— Real Homeowner &nbsp;★★★★★</div>
        </div>

        {/* Bottom benefit strip */}
        <Row gutter={[10, 10]} style={{ marginBottom: "16px" }}>
          {BOTTOM_STRIP.map(b => (
            <Col key={b.label} xs={12} md={6}>
              <div style={{ textAlign: "center", padding: "10px 6px" }}>
                <div style={{ fontSize: "20px" }}>{b.icon}</div>
                <div style={{ fontSize: "11px", fontWeight: 700, color: NAVY, marginTop: "4px" }}>{b.label}</div>
                <div style={{ fontSize: "10px", color: MUTED, marginTop: "2px" }}>{b.desc}</div>
              </div>
            </Col>
          ))}
        </Row>

        <div style={{ textAlign: "center", marginBottom: "4px" }}>
          <div style={{ display: "inline-block", background: NAVY, color: "white", borderRadius: "50px", padding: "10px 24px", fontWeight: 800, fontSize: "13px" }}>
            👇 Explore Your Home Portal Below
          </div>
          <div style={{ fontSize: "11px", color: MUTED, marginTop: "10px", fontStyle: "italic" }}>
            Better Home. Better Life. Built Together.
          </div>
        </div>
      </div>
    );
  }

  render() {
    const userLogin = localStorage.getItem("userLogin");
    const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const isNonAgent =
      userDetails &&
      userDetails.userProfile !== "AG" &&
      userDetails.userProfile !== "L";
    const { sectionKey, subId } = (this.props.match && this.props.match.params) || {};

    return (
      <div style={{ backgroundColor: "#F9FAFF", minHeight: "70vh" }}>
        <Row className="margin-body-agent">
          <Col span={24} style={{ marginTop: "1vw", marginBottom: "0.5vw" }}>
            <h2 style={{ color: NAVY, fontWeight: 800 }}>🏡 Home Portal</h2>
          </Col>
        </Row>
        {!userLogin && (
          <Row className="margin-body-agent">
            <Col span={24}>{this.renderHook()}</Col>
          </Row>
        )}
        <Row className="margin-body-agent">
          <Col
            span={24}
            style={{
              background: "white",
              borderRadius: 10,
              marginBottom: "2vw",
              overflow: "hidden",
            }}
          >
            {isNonAgent && (
              <div style={{ padding: "12px 16px 0" }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={this.handleSetupClick}
                  style={{
                    borderRadius: "50px",
                    background: NAVY,
                    border: "none",
                    fontWeight: 700,
                    marginBottom: "8px",
                  }}
                >
                  🏡 Set Up My Home
                </Button>
              </div>
            )}
            <HomeBuddha
              showSetupModal={this.state.showSetupMyHome}
              onCloseSetupModal={() => this.setState({ showSetupMyHome: false })}
              onShowSetupModal={() => this.setState({ showSetupMyHome: true })}
              initialSection={sectionKey}
              initialSubId={subId}
              enableUrlSync={true}
            />
          </Col>
        </Row>
      </div>
    );
  }
}

export default withAppContext(withRouter(HomePortalPage));